import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { CheckCircle2, Loader2, CreditCard, Info, Mail, MailCheck } from 'lucide-react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { api } from '../lib/api';
import { useSettings } from '../context/SettingsContext';

const inputCls =
  'bg-white/5 border-white/15 text-white placeholder:text-white/40 focus-visible:ring-[#00b4d8] focus-visible:ring-offset-0 rounded-xl h-11';

export const BookingModal = ({ open, onOpenChange, tripSlug, tripName }) => {
  const { settings } = useSettings();
  const [form, setForm] = useState({
    people_count: '2',
    first_name: '',
    last_name: '',
    date_of_birth: '',
    phone: '',
    email: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null); // { booking_ref }
  const [quote, setQuote] = useState(null);
  const [paid, setPaid] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [linkSent, setLinkSent] = useState(false);

  // Once we have a booking_ref + PayPal is enabled, fetch the computed amount
  useEffect(() => {
    if (!success?.booking_ref || !settings?.paypal_enabled || !settings?.paypal_public_client_id) return;
    api.get(`/payments/paypal/quote/${success.booking_ref}`).then(({ data }) => setQuote(data)).catch(() => setQuote(null));
  }, [success?.booking_ref, settings?.paypal_enabled, settings?.paypal_public_client_id]);

  const reset = () => {
    setForm({ people_count: '2', first_name: '', last_name: '', date_of_birth: '', phone: '', email: '' });
    setSuccess(null);
    setError('');
    setSubmitting(false);
    setQuote(null);
    setPaid(false);
    setEmailing(false);
    setLinkSent(false);
  };

  const handleClose = (next) => {
    if (!next) setTimeout(reset, 300);
    onOpenChange(next);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.first_name.trim()) return 'Please enter your first name.';
    if (!form.last_name.trim()) return 'Please enter your last name.';
    if (!form.date_of_birth) return 'Please select your date of birth.';
    if (!form.phone.trim()) return 'Please enter a phone number.';
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) return 'Please enter a valid email address.';
    return '';
  };

  const submit = async (e) => {
    e?.preventDefault();
    const v = validate();
    if (v) { setError(v); return; }
    setError('');
    setSubmitting(true);
    try {
      const { data } = await api.post('/bookings/trip', {
        trip_slug: tripSlug,
        trip_name: tripName,
        people_count: Number(form.people_count),
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        date_of_birth: form.date_of_birth,
        phone: form.phone.trim(),
        email: form.email.trim(),
      });
      setSuccess({ booking_ref: data?.booking_ref });
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Could not submit booking. Please try again.';
      setError(typeof msg === 'string' ? msg : 'Could not submit booking.');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePayPalError = (err) => {
    console.error('PayPal error', err);
    setError('Payment could not be completed. Please try again or contact us via WhatsApp.');
  };

  const sendPaymentLink = async () => {
    if (!success?.booking_ref) return;
    setEmailing(true);
    setError('');
    try {
      const { data } = await api.post('/payments/paypal/email-link', {
        booking_ref: success.booking_ref,
        return_url: `${window.location.origin}/trips`,
        cancel_url: `${window.location.origin}/trips`,
      });
      if (data?.ok) setLinkSent(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Could not send the payment link. Please try again.';
      setError(typeof msg === 'string' ? msg : 'Could not send the payment link.');
    } finally {
      setEmailing(false);
    }
  };

  const payPalEnabled =
    !!settings?.paypal_enabled &&
    !!settings?.paypal_public_client_id &&
    !!success?.booking_ref &&
    !!quote &&
    Number(quote.amount) > 0 &&
    !paid;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        data-testid="booking-modal"
        className="max-w-lg bg-[#07101f] border-white/12 text-white p-0 overflow-hidden"
      >
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-white/10">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#00b4d8] font-semibold mb-1.5">
            Oman Trip Booking
          </div>
          <DialogTitle className="font-display text-2xl font-semibold tracking-tight">
            {tripName || 'Reserve your seat'}
          </DialogTitle>
          <DialogDescription className="text-white/60 text-sm">
            Fill in your details and our team will confirm dates by email within a few hours.
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
            <div className="rounded-2xl border border-[#c8e600]/30 bg-[#c8e600]/[0.06] p-5 flex items-start gap-3">
              <CheckCircle2 className="h-6 w-6 text-[#c8e600] shrink-0 mt-0.5" />
              <div>
                <div className="font-display text-lg font-semibold">Thank you — request received!</div>
                <p className="mt-1 text-sm text-white/70">
                  Your booking reference is{' '}
                  <span className="font-mono text-[#c8e600]">{success.booking_ref}</span>. We just emailed our
                  reservations team and will reach out shortly to confirm availability.
                </p>
              </div>
            </div>

            {paid ? (
              <div className="rounded-2xl border border-[#0078d4]/30 bg-[#0078d4]/[0.08] p-5 flex items-start gap-3">
                <CheckCircle2 className="h-6 w-6 text-[#0078d4] shrink-0 mt-0.5" />
                <div>
                  <div className="font-display text-lg font-semibold">Payment received</div>
                  <p className="mt-1 text-sm text-white/70">
                    PayPal confirmed your payment for booking{' '}
                    <span className="font-mono text-[#c8e600]">{success.booking_ref}</span>. See you on the dive boat!
                  </p>
                </div>
              </div>
            ) : payPalEnabled ? (
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-[#00b4d8] font-semibold">Choose how to pay</div>
                  <div className="text-xs text-white/55">
                    {quote.deposit_pct < 100 ? `${quote.deposit_pct}% deposit` : 'Full payment'}
                  </div>
                </div>
                <div className="text-sm text-white/85 mb-4">
                  <div className="font-display text-2xl font-semibold text-white">
                    {quote.currency} {quote.amount}
                  </div>
                  <div className="text-xs text-white/55 mt-0.5">
                    ≈ AED {(quote.deposit_aed ?? 0).toFixed(2)} of AED {(quote.aed_total ?? 0).toFixed(2)} total
                    {quote.deposit_pct < 100 && ' — remainder due offline'}
                  </div>
                </div>

                {/* Option 1 — Pay here (in-page PayPal) */}
                <div className="mb-3">
                  <div className="flex items-center gap-2 mb-2 text-xs text-white/65">
                    <CreditCard className="h-3.5 w-3.5 text-[#0078d4]" /> Pay here on this page
                  </div>
                  <PayPalScriptProvider
                    options={{
                      'client-id': settings.paypal_public_client_id,
                      currency: quote.currency,
                      intent: 'capture',
                    }}
                  >
                    <PayPalButtons
                      data-testid="booking-paypal-buttons"
                      style={{ layout: 'vertical', color: 'gold', shape: 'rect', label: 'pay' }}
                      createOrder={async () => {
                        const { data } = await api.post('/payments/paypal/create-order', {
                          booking_ref: success.booking_ref,
                        });
                        return data.id;
                      }}
                      onApprove={async (data) => {
                        const { data: cap } = await api.post('/payments/paypal/capture', {
                          booking_ref: success.booking_ref,
                          order_id: data.orderID,
                        });
                        if (cap?.paid || cap?.already_paid) setPaid(true);
                      }}
                      onError={handlePayPalError}
                    />
                  </PayPalScriptProvider>
                </div>

                {/* Separator */}
                <div className="flex items-center gap-2 my-3">
                  <div className="flex-1 h-px bg-white/10" />
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">or</div>
                  <div className="flex-1 h-px bg-white/10" />
                </div>

                {/* Option 2 — Email a payment link */}
                {linkSent ? (
                  <div data-testid="booking-link-sent" className="rounded-xl border border-[#c8e600]/30 bg-[#c8e600]/[0.06] p-3 flex items-start gap-2 text-sm text-white/80">
                    <MailCheck className="h-4 w-4 text-[#c8e600] shrink-0 mt-0.5" />
                    <div>
                      A PayPal payment link was emailed to <span className="text-white font-medium">{form.email}</span>. You can close this window and pay later — your seat is held.
                    </div>
                  </div>
                ) : (
                  <Button
                    data-testid="booking-email-link"
                    onClick={sendPaymentLink}
                    disabled={emailing}
                    variant="outline"
                    className="w-full border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white rounded-xl h-12 text-sm font-semibold"
                  >
                    {emailing ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending link…</>
                    ) : (
                      <><Mail className="mr-2 h-4 w-4" /> Email me a PayPal payment link</>
                    )}
                  </Button>
                )}
              </div>
            ) : (
              <Button
                data-testid="booking-pay-now-placeholder"
                disabled
                className="w-full bg-white/8 text-white/55 cursor-not-allowed rounded-xl h-12 text-base"
              >
                <CreditCard className="mr-2 h-5 w-5" /> Online payment coming soon
              </Button>
            )}

            {!paid && settings?.paypal_enabled && quote && (
              <div className="flex items-start gap-2 text-[11px] text-white/45">
                <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                <span>
                  Prices on the site are in AED. PayPal charges in {quote.currency} using a {quote.fx_rate} AED/USD rate.
                </span>
              </div>
            )}

            <Button
              data-testid="booking-close"
              variant="outline"
              onClick={() => handleClose(false)}
              className="w-full border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white rounded-xl h-11"
            >
              Close
            </Button>
            {error && (
              <div className="rounded-xl border border-red-400/30 bg-red-400/10 text-red-200 text-sm px-3 py-2">{error}</div>
            )}
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label htmlFor="people_count" className="text-white/75 text-xs uppercase tracking-wider">
                  Number of people
                </Label>
                <Select value={form.people_count} onValueChange={(v) => set('people_count', v)}>
                  <SelectTrigger id="people_count" data-testid="booking-people-count" className={`${inputCls} mt-1.5 w-full`}>
                    <SelectValue placeholder="Choose headcount" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#07101f] border-white/15 text-white">
                    {[2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map((n) => (
                      <SelectItem key={n} value={String(n)} className="focus:bg-white/10 focus:text-white">
                        {n} people
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="first_name" className="text-white/75 text-xs uppercase tracking-wider">First name</Label>
                <Input id="first_name" data-testid="booking-first-name" className={`${inputCls} mt-1.5`} value={form.first_name} onChange={(e) => set('first_name', e.target.value)} placeholder="Sarah" autoComplete="given-name" />
              </div>
              <div>
                <Label htmlFor="last_name" className="text-white/75 text-xs uppercase tracking-wider">Last name</Label>
                <Input id="last_name" data-testid="booking-last-name" className={`${inputCls} mt-1.5`} value={form.last_name} onChange={(e) => set('last_name', e.target.value)} placeholder="Al Marri" autoComplete="family-name" />
              </div>

              <div>
                <Label htmlFor="dob" className="text-white/75 text-xs uppercase tracking-wider">Date of birth</Label>
                <Input id="dob" data-testid="booking-dob" type="date" className={`${inputCls} mt-1.5`} value={form.date_of_birth} onChange={(e) => set('date_of_birth', e.target.value)} max={new Date().toISOString().slice(0, 10)} />
              </div>
              <div>
                <Label htmlFor="phone" className="text-white/75 text-xs uppercase tracking-wider">Phone</Label>
                <Input id="phone" data-testid="booking-phone" className={`${inputCls} mt-1.5`} value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+971 50 123 4567" autoComplete="tel" type="tel" />
              </div>

              <div className="col-span-2">
                <Label htmlFor="email" className="text-white/75 text-xs uppercase tracking-wider">Email</Label>
                <Input id="email" data-testid="booking-email" className={`${inputCls} mt-1.5`} value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="you@example.com" autoComplete="email" type="email" />
              </div>
            </div>

            {error && (
              <div data-testid="booking-error" className="rounded-xl border border-red-400/30 bg-red-400/10 text-red-200 text-sm px-3 py-2">{error}</div>
            )}

            <div className="pt-2 grid grid-cols-1 gap-2">
              <Button
                type="submit"
                data-testid="booking-submit"
                disabled={submitting}
                className="w-full bg-[#0078d4] hover:bg-[#0066b8] text-white font-semibold rounded-xl h-12 text-base shadow-lg shadow-[#0078d4]/30 disabled:opacity-60"
              >
                {submitting ? (<><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Sending…</>) : ('Send booking request')}
              </Button>
              <p className="text-[11px] text-white/45 text-center">
                We never share your details — used only to confirm your booking.
              </p>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
